<template>
  <section class="border bg-white rounded-md p-3">
    <div class="text-xl font-bold">{{ $t('pages.game.goals.label') }}</div>
    <h2 class="mt-1 text-sm text-slate-500">
      {{ $t('pages.game.goals.heading', { home: game.home?.name, away: game.away?.name, date: useDate(game?.startDate).format('HH:mm YYYY-MM-DD') }) }}
    </h2>
    <div class="mt-3 space-y-6">
      <div v-for="period in timelineData" :key="period.label" class="space-y-4">
        <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div class="h-px bg-slate-300" />
          <h3 class="font-bold text-slate-900">{{ period.label }}</h3>
          <div class="h-px bg-slate-300" />
        </div>
        <div class="space-y-3">
          <div
            v-for="(event, index) in period.events"
            :key="`${period.label}-${event.minute}-${event.scorer}`"
            class="grid grid-cols-[1fr_auto_1fr] items-start gap-3"
          >
            <div v-if="event.side === 'home'" class="pt-1 text-right">
              <p class="text-xl font-bold leading-none text-slate-900">
                <span class="text-emerald-500">{{ event.homeScore }}</span>
                <span class="mx-1 text-slate-900">-</span>
                <span>{{ event.awayScore }}</span>
              </p>
              <p class="mt-1 font-bold leading-tight text-slate-900">
                {{ event.scorer }}
                <span v-if="event.penalty" class="ml-1 text-xs font-normal text-slate-500">(P)</span>
                <span v-if="event.owngoal" class="ml-1 text-xs font-normal text-red-600">(OG)</span>
              </p>
              <p class="text-sm text-slate-500">{{ event.assist }}</p>
            </div>
            <div v-else />
            <div class="flex flex-col items-center">
              <span
                class="inline-flex py-1 min-w-12 items-center justify-center rounded-md bg-slate-100 px-3 text-sm font-bold text-slate-500"
              >
                {{ event.minute }}
                <template v-if="event.added">+ {{ event.added }}</template>
              </span>
              <span v-if="index != period.events.length - 1" class="mt-2 h-10 w-px bg-slate-300" />
            </div>
            <div v-if="event.side === 'away'" class="pt-1">
              <p class="text-xl font-bold leading-none text-slate-900">
                <span>{{ event.homeScore }}</span>
                <span class="mx-1 text-slate-900">-</span>
                <span class="text-emerald-500">{{ event.awayScore }}</span>
              </p>
              <p class="mt-1 font-bold leading-tight text-slate-900">
                {{ event.scorer }}
                <span v-if="event.penalty" class="ml-1 text-xs font-normal text-slate-500">(P)</span>
                <span v-if="event.owngoal" class="ml-1 text-xs font-normal text-red-600">(OG)</span>
              </p>
              <p class="text-sm text-slate-500">{{ event.assist }}</p>
            </div>
            <div v-else />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  game: {
    type: Object,
    default: () => ({})
  }
})

const goals = computed(() => props.game.goals.map(goal => {
      return {
        period: goal.period,
        type: 'goal',
        minute: goal.time,
        added: goal.timeAdded,
        side: goal.team,
        homeScore: goal.result.home,
        awayScore: goal.result.away,
        scorer: goal.player,
        assist: goal.assist,
        penalty: goal.penalty,
        owngoal: goal.owngoal,
      }
    })
  )

const timelineData = computed(() => {
  return [
    {
      label: '1st',
      events: goals.value.filter(goal => goal.period == 'firstHalf')
    },
    {
      label: '2nd',
      events: goals.value.filter(goal => goal.period == 'secondHalf')
    },
    {
      label: 'ET',
      events: goals.value.filter(goal => goal.period == 'extraTime')
    },
    {
      label: 'Penalties',
      events: goals.value.filter(goal => goal.period == 'penalties')
    },
    {
      label: 'Not in game',
      events: goals.value.filter(goal => goal.period == 'notInGame')
    }
  ].filter(period => period.events.length)
})
</script>
